import React, { useState, useCallback } from 'react';
import {
  Datagrid,
  TextField,
  List,
  TopToolbar,
  Button,
  SaveButton,
  Filter,
  TextInput,
  useListContext,
  useUnselectAll
} from 'react-admin';
import { makeStyles } from '@material-ui/core';
import Empty from '../../core/components/list/Empty';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import ButtonMaterial from '@material-ui/core/Button';
import DialogTitleWithCloseButton from '../../core/components/DialogTitleWithCloseButton';
import {
  predictiveSearchFormatter,
  predictiveSearchParser
} from '../../core/utils';
import StatusSelectField from '../../core/components/StatusSelectField';
import { passthroughFundStatusLookup } from '../funds/passthrough/PassthroughFundStatusLookups';
import PassthroughApi from '../../core/apis/passthrough/PassthroughApi';
import Confirm from '../../core/components/forked/layout/Confirm';

const useStyles = makeStyles({
  connectButton: {
    border: '1px solid #E0E0E0',
    background: '#FFFFFF',
    marginLeft: '10px'
  },
  text: {
    color: '#424242',
    fontSize: '14px',
    fontWeight: '400',
    display: 'inline-block',
    fontFamily: '"Segoe UI",Arial,sans-serif',
    marginBottom: '5px'
  }
});

const ConditionalActions = ({
  handleConnectButtonClick,
  handleDisconnectButtonClick
}) => {
  const { total, data } = useListContext();

  return total > 0 ? (
    <DisconnectButtonAction
      handleDisconnectButtonClick={handleDisconnectButtonClick}
      fundMappingId={data[0]?.fundMappingId}
    />
  ) : (
    <ConnectButtonAction handleConnectButtonClick={handleConnectButtonClick} />
  );
};

const PassthroughTab = ({ fundId }) => {
  const [showConnectDialog, setShowConnectDialog] = useState(false);
  const [listKey, setListKey] = useState(0);
  const [openOptOutModal, setOpenOptOutModal] = useState(false);
  const [pendingDisconnectId, setPendingDisconnectId] = useState(null);

  const unselectAll = useUnselectAll('passthroughConnect');

  const handleConnectButtonClick = useCallback(() => {
    setShowConnectDialog(prevState => !prevState);
    unselectAll();
  }, [unselectAll]);

  const handleDisconnectButtonClick = useCallback(fundMappingId => {
    setPendingDisconnectId(fundMappingId);
    setOpenOptOutModal(true);
  }, []);

  const handleAddButtonClick = useCallback(
    async selectedIds => {
      if (!selectedIds || selectedIds.length === 0) return;

      const ptFundId = selectedIds[0];

      const api = new PassthroughApi();
      await api.createFundMapping(fundId, ptFundId);

      setListKey(prev => prev + 1);
      setShowConnectDialog(false);
    },
    [fundId]
  );

  const handleClose = useCallback(() => {
    setShowConnectDialog(false);
  }, []);

  return (
    <>
      <List
        key={listKey}
        style={{ margin: '0px', width: '100%' }}
        basePath="/passthrough"
        resource="passthrough"
        sort={{ field: 'investorName', order: 'ASC' }}
        title=" "
        filter={{ icFundId: fundId }}
        actions={
          <ConditionalActions
            handleConnectButtonClick={handleConnectButtonClick}
            handleDisconnectButtonClick={handleDisconnectButtonClick}
          />
        }
        empty={
          <Empty
            emptyText="No Fund connected"
            buttons={[
              {
                label: 'Connect',
                onClick: handleConnectButtonClick
              }
            ]}
          />
        }
        bulkActionButtons={false}
        pagination={false}
      >
        <Datagrid>
          <TextField
            source="ptFundName"
            label="Passthrough Fund Name"
            sortable={true}
            id="fund name"
          />
          <StatusSelectField
            source="ptFundStatus"
            label="Passthrough Fund Status"
            id="fund status"
            choices={passthroughFundStatusLookup}
          />
        </Datagrid>
      </List>
      {showConnectDialog && (
        <Dialog
          fullWidth
          open={true}
          aria-label={`connect fund`}
          maxWidth="md"
          onClose={(event, reason) => {
            if (reason === 'backdropClick') return null;
            handleClose();
          }}
        >
          <DialogTitleWithCloseButton onClose={handleClose}>
            {'Connect to Passthrough Fund'}
          </DialogTitleWithCloseButton>
          <DialogContent>
            <List
              style={{ margin: '0px', width: '100%' }}
              basePath="/passthroughConnect"
              resource="passthroughConnect"
              sort={{ field: 'name', order: 'ASC' }}
              filters={<FundFilters />}
              title=" "
              actions={<></>}
              empty={
                <Empty
                  emptyText="No Funds"
                  buttons={[
                    {
                      label: 'Cancel',
                      onClick: handleClose
                    }
                  ]}
                />
              }
              bulkActionButtons={<></>}
              pagination={false}
            >
              <DatagridWithDialogActionsWithSelection
                handleAddButtonClick={handleAddButtonClick}
                onClose={handleClose}
              />
            </List>
          </DialogContent>
        </Dialog>
      )}
      <Confirm
        onClose={() => setOpenOptOutModal(false)}
        showCancel={true}
        title={'Confirm Disconnect'}
        isOpen={openOptOutModal}
        content={
          'Are you sure you want to continue? Users will no longer be able to subscribe to this fund.'
        }
        confirm={'Confirm'}
        cancel={'Cancel'}
        confirmColor={'primary'}
        onConfirm={async () => {
          if (pendingDisconnectId) {
            const api = new PassthroughApi();
            await api.deleteFundMapping(pendingDisconnectId);
            setListKey(prev => prev + 1);
          }
          setOpenOptOutModal(false);
        }}
      />
    </>
  );
};

const ConnectButtonAction = props => {
  const classes = useStyles();
  return (
    <TopToolbar>
      <Button
        color="primary"
        onClick={props.handleConnectButtonClick}
        label="Connect"
        disableFocusRipple
        className={classes.connectButton}
      />
    </TopToolbar>
  );
};

const DisconnectButtonAction = props => {
  const classes = useStyles();
  return (
    <TopToolbar>
      <Button
        color="primary"
        onClick={() => props.handleDisconnectButtonClick(props.fundMappingId)}
        label="Disconnect"
        disableFocusRipple
        className={classes.connectButton}
      />
    </TopToolbar>
  );
};

const FundFilters = props => (
  <Filter variant="standard" {...props}>
    <TextInput
      source="passthroughFundId"
      alwaysOn
      parse={predictiveSearchParser}
      format={predictiveSearchFormatter}
      label="Search"
    />
  </Filter>
);

const DatagridWithDialogActionsWithSelection = ({
  handleAddButtonClick,
  onClose
}) => {
  const { selectedIds, onSelect } = useListContext();

  const handleAdd = () => {
    handleAddButtonClick(selectedIds);
  };

  const rowStyle = record => ({
    backgroundColor: selectedIds.includes(record.id) ? '#f0f0f0' : 'white'
  });

  const handleRowClick = id => {
    // Toggle selection, but allow only one
    const newSelection = selectedIds.includes(id) ? [] : [id];
    onSelect(newSelection);
  };

  return (
    <>
      <Datagrid rowClick={id => handleRowClick(id)} rowStyle={rowStyle}>
        <TextField
          source="name"
          label="Passthrough Fund Name"
          sortable={true}
          id="fund Name"
        />
        <StatusSelectField
          source="status"
          label="Passthrough Fund Status"
          sortable={true}
          id="fund status"
          choices={passthroughFundStatusLookup}
        />
      </Datagrid>
      <DialogActions>
        <SaveButton
          style={{ minWidth: '82px' }}
          label={'Add'}
          icon={<></>}
          id="connect-button"
          handleSubmitWithRedirect={handleAdd}
          disabled={selectedIds.length === 0}
        />
        <ButtonMaterial
          onClick={onClose}
          variant="outlined"
          color="primary"
          id="cancel-button"
        >
          Cancel
        </ButtonMaterial>
      </DialogActions>
    </>
  );
};

export default PassthroughTab;
